import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Shield, Search, X, ChevronDown, Save, RotateCcw, Info,
  Lock, AlertCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";

// =============================================================================
// AdminPermissionsScreen — /admin/permissions
// T-129 — director-only role × permission matrix editor.
//
// What this is: a grid where rows = capabilities (grouped by category),
// columns = roles (Director / Manager / Supervisor / Field / Subcontractor).
// Tickboxes in each cell. The director column is locked because
// app.has_permission() short-circuits to true for directors anyway —
// this is just visual reinforcement that you can't lock yourself out.
//
// What this is NOT: an enforcement engine. Toggling a checkbox writes to
// app.role_permissions, but no existing gate has been rewired to call
// app.has_permission() yet. That migration happens incrementally in T-149.
// Until then, this screen is a configurable preview of the matrix.
// =============================================================================

const ROLES = ["director", "manager", "supervisor", "field", "subcontractor"] as const;
type Role = typeof ROLES[number];

const ROLE_LABELS: Record<Role, string> = {
  director:      "Director",
  manager:       "Manager",
  supervisor:    "Supervisor",
  field:         "Field",
  subcontractor: "Subcontractor",
};

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  director:      "Full access — column locked",
  manager:       "Day-to-day operations + financials",
  supervisor:    "Site lead — read + limited write",
  field:         "Frontline — own records + HSE submit",
  subcontractor: "External — narrow read-only by default",
};

interface PermissionRow {
  permission_key: string;
  display_name:   string;
  category:       string;
  description:    string | null;
  sort_order:     number;
}

interface RolePermissionRow {
  role_name:      string;
  permission_key: string;
  allowed:        boolean;
}

// Local key for the editor's pending-changes map.
type GrantKey = `${Role}:${string}`;

export default function AdminPermissionsScreen() {
  const me = useRequireAuth();
  const qc = useQueryClient();
  const isDirector = hasRole(me, "director");

  const [search, setSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Pending changes: keyed by `role:permission_key` → boolean
  const [pending, setPending] = useState<Map<GrantKey, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const { data: perms = [], isLoading: loadingPerms } = useQuery({
    enabled: isDirector,
    queryKey: ["admin_permissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("app")
        .from("permissions")
        .select("permission_key, display_name, category, description, sort_order")
        .order("category")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PermissionRow[];
    },
  });

  const { data: grants = [], isLoading: loadingGrants } = useQuery({
    enabled: isDirector,
    queryKey: ["admin_role_permissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("app")
        .from("role_permissions")
        .select("role_name, permission_key, allowed");
      if (error) throw error;
      return (data ?? []) as RolePermissionRow[];
    },
  });

  // Lookup map: role:key → bool, with director defaulting to true.
  const grantMap = useMemo(() => {
    const m = new Map<GrantKey, boolean>();
    for (const g of grants) m.set(`${g.role_name as Role}:${g.permission_key}`, g.allowed);
    return m;
  }, [grants]);

  function effectiveValue(role: Role, key: string): boolean {
    if (role === "director") return true; // director always wins (matches has_permission)
    const k: GrantKey = `${role}:${key}`;
    if (pending.has(k)) return pending.get(k)!;
    return grantMap.get(k) ?? false;
  }

  function isPending(role: Role, key: string): boolean {
    if (role === "director") return false;
    const k: GrantKey = `${role}:${key}`;
    if (!pending.has(k)) return false;
    return pending.get(k) !== (grantMap.get(k) ?? false);
  }

  function toggleCell(role: Role, key: string) {
    if (role === "director") return; // locked
    const k: GrantKey = `${role}:${key}`;
    const current = pending.has(k) ? pending.get(k)! : (grantMap.get(k) ?? false);
    const next = !current;
    const original = grantMap.get(k) ?? false;
    setPending(prev => {
      const m = new Map(prev);
      if (next === original) m.delete(k); // back to original = no longer pending
      else m.set(k, next);
      return m;
    });
  }

  function discardChanges() {
    setPending(new Map());
  }

  // ---------------------------------------------------------------------------
  // Filter + group permissions for rendering.
  // ---------------------------------------------------------------------------
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: { category: string; rows: PermissionRow[] }[] = [];
    let current: { category: string; rows: PermissionRow[] } | null = null;
    for (const p of perms) {
      const matches = !q
        || p.display_name.toLowerCase().includes(q)
        || p.permission_key.toLowerCase().includes(q)
        || (p.description ?? "").toLowerCase().includes(q)
        || p.category.toLowerCase().includes(q);
      if (!matches) continue;
      if (!current || current.category !== p.category) {
        current = { category: p.category, rows: [] };
        out.push(current);
      }
      current.rows.push(p);
    }
    return out;
  }, [perms, search]);

  // ---------------------------------------------------------------------------
  // Save mutation — upsert one row per pending change.
  // ---------------------------------------------------------------------------
  async function saveChanges() {
    if (pending.size === 0) return;
    setSaving(true);
    try {
      const rows = Array.from(pending.entries()).map(([k, allowed]) => {
        const [role_name, permission_key] = k.split(":") as [string, string];
        return { role_name, permission_key, allowed };
      });
      const { error } = await supabase
        .schema("app")
        .from("role_permissions")
        .upsert(rows, { onConflict: "role_name,permission_key" });
      if (error) throw error;
      setPending(new Map());
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      qc.invalidateQueries({ queryKey: ["admin_role_permissions"] });
    } catch (e: any) {
      alert(`Save failed: ${e?.message ?? String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render guards — director only.
  // ---------------------------------------------------------------------------
  if (!isDirector) {
    return (
      <div className="max-w-md mx-auto p-6 mt-12">
        <Card className="text-center p-6">
          <Shield size={32} className="text-text-muted mx-auto mb-2"/>
          <div className="text-[15px] font-semibold mb-1">Director only</div>
          <div className="text-[12px] text-text-muted">
            Role permission management is restricted to directors.
          </div>
        </Card>
      </div>
    );
  }

  if (loadingPerms || loadingGrants) {
    return <div className="py-20 flex justify-center"><Spinner/></div>;
  }

  // ---------------------------------------------------------------------------
  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-32">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2">
            <Shield size={20} className="text-accent" />
            Role Permissions
          </h1>
          <div className="text-[12px] text-text-muted mt-0.5 max-w-xl">
            Configure what each role can do. The Director column is locked — directors
            always have full access by design. Changes here are saved to the database
            but no existing gate has been migrated to use them yet (that's T-149).
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-ink-raised border border-line rounded-md w-72 max-w-[60vw]">
            <Search size={14} className="text-text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search capabilities…"
              className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-text-muted hover:text-text" title="Clear">
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Role legend strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {ROLES.map(r => (
          <div key={r} className="bg-ink-raised border border-line rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
              {r === "director" && <Lock size={9}/>}
              {ROLE_LABELS[r]}
            </div>
            <div className="text-[11px] text-text-muted mt-1 leading-tight">
              {ROLE_DESCRIPTIONS[r]}
            </div>
          </div>
        ))}
      </div>

      {/* Pending-changes banner */}
      {pending.size > 0 && (
        <div className="mb-3 bg-warn/10 border border-warn/30 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertCircle size={14} className="text-warn"/>
            <span className="text-[13px]">
              <span className="font-semibold">{pending.size}</span> pending change{pending.size === 1 ? "" : "s"} —
              <span className="text-text-muted ml-1">review and save, or discard.</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" icon={RotateCcw} onClick={discardChanges} disabled={saving}>
              Discard
            </Button>
            <Button size="sm" icon={Save} onClick={saveChanges} disabled={saving}>
              {saving ? <Spinner size={12}/> : `Save ${pending.size} change${pending.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {/* Saved flash */}
      {savedFlash && (
        <div className="mb-3 bg-ok/10 border border-ok/30 rounded-md p-2.5 text-[12px] text-ok flex items-center gap-2">
          <Info size={12}/> Changes saved.
        </div>
      )}

      {/* Grid */}
      <Card>
        {grouped.length === 0 ? (
          <EmptyState icon={Search} title="No matches" description={`Nothing matches "${search}".`}/>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-ink-raised z-10">
                <tr className="border-b border-line">
                  <th className="text-left px-4 py-3 font-medium text-text-muted text-[11px] uppercase tracking-wider sticky left-0 bg-ink-raised z-20 min-w-[280px]">
                    Capability
                  </th>
                  {ROLES.map(r => (
                    <th key={r} className="text-center px-3 py-3 font-medium text-text text-[11px] uppercase tracking-wider min-w-[110px]">
                      <div className="flex items-center justify-center gap-1">
                        {r === "director" && <Lock size={10} className="text-text-muted"/>}
                        {ROLE_LABELS[r]}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(group => {
                  const isCollapsed = collapsedGroups.has(group.category);
                  return (
                    <CategoryGroup
                      key={group.category}
                      category={group.category}
                      rows={group.rows}
                      collapsed={isCollapsed}
                      onToggle={() => setCollapsedGroups(prev => {
                        const next = new Set(prev);
                        next.has(group.category) ? next.delete(group.category) : next.add(group.category);
                        return next;
                      })}
                      effectiveValue={effectiveValue}
                      isPending={isPending}
                      onToggleCell={toggleCell}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// CategoryGroup — one collapsible band of permissions sharing a category.
// =============================================================================
function CategoryGroup({
  category, rows, collapsed, onToggle,
  effectiveValue, isPending, onToggleCell,
}: {
  category: string;
  rows: PermissionRow[];
  collapsed: boolean;
  onToggle: () => void;
  effectiveValue: (role: Role, key: string) => boolean;
  isPending:      (role: Role, key: string) => boolean;
  onToggleCell:   (role: Role, key: string) => void;
}) {
  const totalCells = rows.length * (ROLES.length - 1); // exclude director (locked)
  const grantedCount = rows.reduce((acc, r) => {
    let n = 0;
    for (const role of ROLES) {
      if (role === "director") continue;
      if (effectiveValue(role, r.permission_key)) n++;
    }
    return acc + n;
  }, 0);

  return (
    <>
      <tr className="bg-ink-raised/60 border-b border-line/60">
        <td colSpan={1 + ROLES.length} className="px-4 py-2 sticky left-0 bg-ink-raised/60">
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center gap-2 text-left"
          >
            <ChevronDown
              size={14}
              className={`text-text-muted flex-shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`}
            />
            <span className="text-[12px] uppercase tracking-wider text-text font-semibold">{category}</span>
            <span className="text-[11px] text-text-muted ml-2">
              {rows.length} capabilit{rows.length === 1 ? "y" : "ies"} ·
              <span className="font-mono ml-1">{grantedCount}/{totalCells}</span> granted
            </span>
          </button>
        </td>
      </tr>

      {!collapsed && rows.map(r => (
        <tr key={r.permission_key} className="border-b border-line/40 hover:bg-ink-hover/40">
          <td className="px-4 py-2.5 sticky left-0 bg-ink-card hover:bg-ink-hover/40">
            <div className="text-[13px] font-medium text-text">{r.display_name}</div>
            {r.description && (
              <div className="text-[11px] text-text-muted mt-0.5">{r.description}</div>
            )}
            <div className="text-[10px] font-mono text-text-dim mt-0.5">{r.permission_key}</div>
          </td>
          {ROLES.map(role => {
            const value = effectiveValue(role, r.permission_key);
            const pending = isPending(role, r.permission_key);
            const locked = role === "director";
            return (
              <td key={role} className="px-3 py-2 text-center">
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => onToggleCell(role, r.permission_key)}
                  className={[
                    "w-7 h-7 rounded inline-flex items-center justify-center transition-colors",
                    locked
                      ? "bg-accent/15 text-accent cursor-not-allowed"
                      : value
                        ? pending
                          ? "bg-warn/25 text-warn ring-1 ring-warn/50 hover:bg-warn/35"
                          : "bg-ok/15 text-ok hover:bg-ok/25"
                        : pending
                          ? "bg-warn/15 text-warn ring-1 ring-warn/40 hover:bg-warn/25"
                          : "bg-ink hover:bg-ink-hover text-text-dim",
                  ].join(" ")}
                  title={
                    locked
                      ? "Director — always granted"
                      : pending
                        ? "Unsaved change"
                        : value ? "Granted" : "Denied — click to grant"
                  }
                >
                  {value ? "✓" : "—"}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
